import { observable, action } from 'mobx';
import LANGUAGE from '../constants/language';
import axios from 'axios';
import { createIntl, createIntlCache, IntlShape } from 'react-intl';
import Log from '@app/src/common/helpers/log';
import CompanySettingsRepository from '@app/src/common/repository/CompanySettingsRepository';
import { USER_MODULE_KEY } from '@app/src/common/model/AppModel';
import { applyTranslationOverlay } from '@app/src/common/helpers/applyTranslationOverlay';

interface ILocaleStore {
  locale: string;
  messages: Record<string, string>;
  intl: IntlShape;
}

class LocaleStore implements ILocaleStore {
  // This is optional but highly recommended
  // since it prevents memory leak
  cache = createIntlCache();
  locale = this.defaultLanguage;
  messages: Record<string, string> = {};
  @observable intl: IntlShape;
  private loading: Promise<void>;

  constructor() {
    this.init();
  }

  private initIntl() {
    this.intl = createIntl(
      {
        locale: this.locale,
        messages: this.messages
      },
      this.cache
    );
  }

  @action
  init() {
    this.loading = this.fetchTranslation().then(() => this.initIntl());
    return this.loading;
  }

  @action
  async fetchTranslation() {
    try {
      const salt = new Date().getTime();
      const response = await axios.get(
        `/translations/${this.locale}.json?${salt}`
      );
      this.messages = { ...this.messages, ...response.data };
    } catch (error) {
      Log.error(error);
    }
  }

  @action
  async defineLanguage(language: string, overlayPrefixes: string[] = []) {
    try {
      // Overlays and custom translations must land on top of the base texts
      await this.loading;
      if (!language && this.locale) {
        this.locale = this.defaultLanguage;
        await this.init();
      }
      if (language && language !== this.locale) {
        this.locale = language;
        await this.init();
      }
      if (overlayPrefixes.length) {
        this.messages = overlayPrefixes.reduce(
          applyTranslationOverlay,
          this.messages
        );
        this.initIntl();
      }
      const { value } = await CompanySettingsRepository.getSetting(
        USER_MODULE_KEY,
        `PLATFORM_TRANSLATIONS_${this.locale.toUpperCase()}`
      );
      if (value) {
        this.extendTranslations(JSON.parse(value));
      }
    } catch (e) {
      Log.error('Invalid custom translations');
    }
  }

  formatMessage(key: string, values?: Record<string, any>) {
    if (!this.intl) {
      Log.warn("Localization didn't init");
      return key;
    }

    return this.intl.formatMessage({ id: key }, values);
  }

  get defaultLanguage() {
    const current = window.navigator.language.slice(0, 2);
    return [LANGUAGE.en, LANGUAGE.de].includes(current) ? current : LANGUAGE.en;
  }

  extendTranslations(newMessages: Record<string, string>) {
    this.messages = { ...this.messages, ...newMessages };
    this.initIntl();
  }
}

const localeStore = new LocaleStore();
export default localeStore;
